import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Target, HeartPulse, Loader2, Play, RefreshCw, AlertTriangle, CheckCircle2, X, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function SmartPublishingPage() {
  const [tab, setTab] = useState("predict");

  useEffect(() => { document.title = "Smart Publishing | Epidemos"; }, []);

  return (
    <div className="p-6 max-w-none mx-auto">
      <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
        <Sparkles className="w-7 h-7 text-purple-600" /> Smart Publishing
      </h1>
      <p className="text-sm text-slate-500 mb-4">A/B title testing + Performance Predictor + Site Health auto-disable.</p>

      <div className="border-b border-slate-200 mb-4 flex gap-1">
        {[
          { id: "predict", label: "Performance Predictor", icon: Target },
          { id: "ab", label: "A/B Title Testing", icon: TrendingUp },
          { id: "health", label: "Site Health Monitor", icon: HeartPulse },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
              tab === t.id ? "border-purple-600 text-purple-700" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "predict" && <PredictTab />}
      {tab === "ab" && <ABTab />}
      {tab === "health" && <HealthTab />}
    </div>
  );
}

function PredictTab() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [keyword, setKeyword] = useState("");
  const [domain, setDomain] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const predict = async (e) => {
    e?.preventDefault();
    if (!keyword || !content) return;
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/predict/article`, {
        article: { title, content, meta_title: title.substring(0, 60), tags: [keyword] },
        main_keyword: keyword,
        our_domain: domain,
      });
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const posColor = (p) => p === "1-10" ? "bg-green-500" : p === "11-30" ? "bg-amber-500" : "bg-red-500";
  const recColor = (r) => r === "publish" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={predict} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <h2 className="font-bold mb-2">Wpisz dane artykułu</h2>
        <div>
          <label className="text-xs uppercase text-slate-500 block mb-1">Główny keyword</label>
          <input value={keyword} onChange={e => setKeyword(e.target.value)} required
            placeholder='np. "kredyt hipoteczny"'
            className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
        </div>
        <div>
          <label className="text-xs uppercase text-slate-500 block mb-1">Tytuł</label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
        </div>
        <div>
          <label className="text-xs uppercase text-slate-500 block mb-1">Nasza domena (opcjonalne)</label>
          <input value={domain} onChange={e => setDomain(e.target.value)}
            placeholder='np. "moja-strona.pl"'
            className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
        </div>
        <div>
          <label className="text-xs uppercase text-slate-500 block mb-1">Content HTML (lub paste)</label>
          <textarea value={content} onChange={e => setContent(e.target.value)} required rows={8}
            placeholder="<p>Lead...</p><h2>...</h2>..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-xs" />
        </div>
        <button type="submit" disabled={loading || !keyword || !content}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
          Przewiduj pozycję
        </button>
      </form>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        {!result ? (
          <div className="text-slate-500 text-sm text-center py-8">Wypełnij formularz po lewej żeby zobaczyć predykcję</div>
        ) : !result.ok ? (
          <div className="text-red-600">{result.error}</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase text-slate-500">Predykcja pozycji</div>
                <div className="text-4xl font-bold mt-1">
                  <span className={`px-3 py-1 rounded text-white ${posColor(result.predicted_position)}`}>
                    {result.predicted_position}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1">Confidence: {result.confidence}%</div>
              </div>
              <div className={`px-3 py-1 rounded font-bold ${recColor(result.recommendation)}`}>
                {result.recommendation === "publish" ? "✓ PUBLIKUJ" : "⚠ POPRAW NAJPIERW"}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4 text-sm">
              <div className="p-2 bg-slate-50 rounded">
                <div className="text-xs text-slate-500">Quality</div>
                <div className="font-bold">{result.scores?.quality}/100</div>
              </div>
              <div className="p-2 bg-slate-50 rounded">
                <div className="text-xs text-slate-500">Topical Authority</div>
                <div className="font-bold">{result.scores?.topical_authority}/100</div>
              </div>
              <div className="p-2 bg-slate-50 rounded">
                <div className="text-xs text-slate-500">Word count</div>
                <div className="font-bold">{result.scores?.word_count}/100</div>
              </div>
            </div>

            {result.weak_spots?.length > 0 && (
              <div className="mb-3">
                <div className="font-bold text-sm text-amber-700 mb-1">Słabe punkty do poprawy:</div>
                <ul className="text-xs space-y-1 text-slate-700">
                  {result.weak_spots.map((w, i) => <li key={i}>• {w}</li>)}
                </ul>
              </div>
            )}

            {result.competitors_top3?.length > 0 && (
              <div>
                <div className="font-bold text-sm mb-1">Konkurencja TOP 3 (DA avg: {result.competitor_authority_avg}):</div>
                <ul className="text-xs space-y-1">
                  {result.competitors_top3.map((c, i) => (
                    <li key={i}>
                      <span className="font-mono text-slate-400">#{c.position}</span>{" "}
                      <a href={c.url} target="_blank" rel="noopener" className="text-blue-600 hover:underline">{c.title}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ABTab() {
  const [topic, setTopic] = useState("");
  const [original, setOriginal] = useState("");
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [collectResult, setCollectResult] = useState(null);

  const generate = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/ab-title/generate-variants`, {
        topic, original_title: original, num_variants: 3,
      });
      setVariants(r.data.variants || []);
    } finally { setLoading(false); }
  };

  const collect = async () => {
    setCollecting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/ab-title/collect-results?min_age_days=14`);
      setCollectResult(r.data);
    } finally { setCollecting(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-bold mb-3">Generator wariantów title</h2>
        <form onSubmit={generate} className="space-y-3">
          <input value={topic} onChange={e => setTopic(e.target.value)} required
            placeholder="Temat (np. kredyt hipoteczny)"
            className="w-full px-3 py-2 border border-slate-300 rounded" />
          <input value={original} onChange={e => setOriginal(e.target.value)}
            placeholder="Oryginalny tytuł"
            className="w-full px-3 py-2 border border-slate-300 rounded" />
          <button type="submit" disabled={loading || !topic}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Wygeneruj 3 warianty
          </button>
        </form>
        {variants.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs uppercase text-slate-500">Warianty (Grok):</div>
            {variants.map((v, i) => (
              <div key={i} className="p-2 bg-slate-50 rounded text-sm">
                <span className="font-mono text-slate-400 mr-2">{i + 1}.</span>{v}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-bold mb-3">Zbierz wyniki A/B (14d+)</h2>
        <p className="text-sm text-slate-500 mb-3">Cron weekly sprawdza posty starsze niż 14 dni i wyłania zwycięskie warianty.</p>
        <button onClick={collect} disabled={collecting}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50">
          {collecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Uruchom collect-results
        </button>
        {collectResult && (
          <div className="mt-4 p-3 bg-slate-50 rounded text-sm">
            <div>Sprawdzonych: <strong>{collectResult.checked}</strong></div>
            <div>Wyłonionych winners: <strong className="text-green-700">{collectResult.decided}</strong></div>
            <div>Wciąż running: <strong>{collectResult.still_running}</strong></div>
            {collectResult.details?.length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase text-slate-500 mb-1">Winners:</div>
                <ul className="space-y-1 text-xs">
                  {collectResult.details.slice(0, 10).map((d, i) => (
                    <li key={i}>{d.title} — pos #{d.position} (score {d.score})</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function HealthTab() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reEnabling, setReEnabling] = useState({});

  const check = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/health/check-and-disable?days=7&fail_threshold=0.5&min_attempts=5`);
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const reEnable = async (siteId) => {
    setReEnabling({ ...reEnabling, [siteId]: true });
    try {
      await axios.post(`${BACKEND_URL}/api/admin/health/re-enable/${siteId}`);
      alert(`Re-enabled ${siteId}`);
      check();
    } finally { setReEnabling(prev => { const c = { ...prev }; delete c[siteId]; return c; }); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-bold">Site Health Check</h2>
          <p className="text-xs text-slate-500">7d window, fail rate &gt;50%, min 5 prób → auto-disable.</p>
        </div>
        <button onClick={check} disabled={loading}
          className="px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <HeartPulse className="w-4 h-4" />}
          Uruchom check-and-disable
        </button>
      </div>
      {result && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <div className="text-xs text-red-700">Auto-disabled</div>
              <div className="text-2xl font-bold text-red-700">{result.disabled_count}</div>
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <div className="text-xs text-green-700">Healthy</div>
              <div className="text-2xl font-bold text-green-700">{result.healthy_count}</div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded">
              <div className="text-xs text-slate-700">Skipped (low attempts)</div>
              <div className="text-2xl font-bold text-slate-700">{result.skipped_low_attempts}</div>
            </div>
          </div>
          {result.disabled?.length > 0 && (
            <div>
              <h3 className="font-bold text-sm mb-2 text-red-700">Wyłączone portale ({result.disabled.length}):</h3>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2">Portal</th>
                    <th className="text-right p-2">Fail %</th>
                    <th className="text-right p-2">Total / Failed</th>
                    <th className="text-left p-2">Last error</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {result.disabled.map(s => (
                    <tr key={s.site_id} className="border-t border-slate-100">
                      <td className="p-2 font-medium">{s.name}</td>
                      <td className="p-2 text-right font-bold text-red-700">{s.fail_rate}%</td>
                      <td className="p-2 text-right text-xs">{s.total} / {s.failed}</td>
                      <td className="p-2 text-xs text-slate-500">{s.last_error}</td>
                      <td className="p-2">
                        <button onClick={() => reEnable(s.site_id)} disabled={reEnabling[s.site_id]}
                          className="px-2 py-1 text-xs bg-green-50 hover:bg-green-100 text-green-700 rounded">
                          {reEnabling[s.site_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Re-enable"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
